<template>
  <component
    :is="href ? 'a' : 'button'"
    :class="modularizedClasses"
    :href="href"
    :type="!href && type"
    :disabled="!href && disabled"
    @click="emitClick"
  >
    <slot />
  </component>
</template>

<script>
export default {
  name: 'OvpButton',

  props: {
    type: {
      type: String,
      default: 'button',
    },
    primary: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    plain: {
      type: Boolean,
      default: false,
    },
    negative: {
      type: Boolean,
      default: false,
    },
    tiny: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String,
      default: undefined,
    },
  },

  computed: {
    modularizedClasses() {
      return ['primary', 'secondary', 'disabled', 'plain', 'tiny', 'negative'].filter(c => this[c]).concat(['button'])
    },
  },

  methods: {
    emitClick(event) {
      if (this.href && this.disabled) {
        event.preventDefault()
        return
      }
      this.$emit('click', event)
    },
  },
}
</script>

<style name="mobile" scoped>
.button {
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;

  font-family: 'Open Sans Bold', sans-serif;
  height: 50px;
  min-height: 50px;
  width: 100%;
  border: 1px solid var(--c-original-green);
  padding: 5px 26px;
  margin-bottom: 20px;
  font-size: 15px;
  outline: none;
  background: none;
  border-radius: 25px;
  transition: all 0.25s ease-in-out;

  span {
    align-self: center;
  }

  &:not(:disabled):hover {
    cursor: pointer;
  }

  &:disabled,
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: transparent;
  }

  &.primary {
    background-color: var(--c-original-green);
    color: var(--c-white);
    fill: var(--c-white);

    &:not(:disabled):hover {
      border-color: var(--c-sanitas-green);
      background-color: var(--c-sanitas-green);
      color: var(--c-white);
      fill: var(--c-white);

      &.negative {
        border-color: var(--c-white);
        background-color: var(--c-white);
        color: var(--c-original-green);
        fill: var(--c-original-green);
      }
    }
  }

  &.secondary {
    color: var(--c-original-green);
    fill: var(--c-original-green);

    &.negative {
      color: var(--c-white);
      fill: var(--c-white);
      border: 2px solid var(--c-white);
    }

    &:not(:disabled):hover {
      color: var(--c-white);
      fill: var(--c-white);
      border-color: var(--c-original-green);
      background-color: var(--c-original-green);

      &.negative {
        color: var(--c-medium-gray);
        fill: var(--c-medium-gray);
        background-color: var(--c-white);
        border: 2px solid var(--c-white);
      }
    }
  }

  &.plain {
    min-width: 70px;
    padding: 5px;
    border-color: transparent;
    border-radius: 0;
    color: var(--c-black);

    &:not(:disabled):hover {
      color: var(--c-sanitas-green);
      fill: var(--c-sanitas-green);
    }
  }

  &.tiny {
    min-height: 30px;
    max-height: 30px;
    font-family: 'Open Sans', sans-serif;
    font-size: 12px;
  }

  .spin {
    position: absolute;
    left: 10px;
    animation: spin 1.5s linear infinite;
  }

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
}

@media (min-width: 940px) {
  .button {
    min-width: 120px;
    width: auto;
    margin-bottom: 0;
    flex: none;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .button {
    &.primary {
      background-color: var(--c-original-green);
      color: var(--c-white);
      fill: var(--c-white);

      &:not(:disabled):focus {
        border-color: var(--c-sanitas-green);
        background-color: var(--c-sanitas-green);
        color: var(--c-white);
        fill: var(--c-white);

        &.negative {
          border-color: var(--c-white);
          background-color: var(--c-white);
          color: var(--c-original-green);
          fill: var(--c-original-green);
        }
      }
    }

    &.secondary {
      color: var(--c-original-green);
      fill: var(--c-original-green);

      &.negative {
        color: var(--c-white);
        fill: var(--c-white);
        border: 2px solid var(--c-white);
      }

      &:not(:disabled):focus {
        color: var(--c-white);
        fill: var(--c-white);
        border-color: var(--c-original-green);
        background-color: var(--c-original-green);

        &.negative {
          color: var(--c-medium-gray);
          fill: var(--c-medium-gray);
          background-color: var(--c-white);
          border: 2px solid var(--c-white);
        }
      }
    }

    &.plain {
      min-width: 70px;
      padding: 5px;
      border-color: transparent;
      border-radius: 0;

      &:not(:disabled):focus {
        color: var(--c-sanitas-green);
        fill: var(--c-sanitas-green);
      }
    }
  }
}
</style>
